import { useCallback, useEffect, useRef, useState } from "react";

import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { useToastStore } from "@/components/PersistentToast/store/toast";
import { useIsSessionActive } from "@/hooks/useIsSessionActive";
import { logoutUser } from "@/utils/tokenManager";

import { CaseSection } from "./CaseSection";
import { FileItem } from "./FileItem";
import { selectPickerTarget, useBundleUploads } from "./store/useBundleUploads";
import { groupRowsByCase } from "./utils/groupRowsByCase";

// MDRS-550: rows come from the bundle-upload store (not props) and are keyed
// by row id, so another upload or another case's recovered tasks add rows
// rather than remounting these — and two files with one name stay distinct.
// Each case is a collapsible section under its label, the case on screen
// first and open.
export const FileList = () => {
  const rows = useBundleUploads((state) => state.rows);
  const activeTarget = useBundleUploads((state) => state.activeTarget);
  const caseOpen = useBundleUploads((state) => state.caseOpen);
  const setCaseOpen = useBundleUploads((state) => state.setCaseOpen);
  const setTaskId = useBundleUploads((state) => state.setTaskId);
  const setBusy = useBundleUploads((state) => state.setBusy);
  const setStatus = useBundleUploads((state) => state.setStatus);
  const removeRow = useBundleUploads((state) => state.removeRow);
  const isPickerShown = useBundleUploads(
    (state) => selectPickerTarget(state) !== null
  );
  // Row id → whether that row finished needing review.
  const [completedRows, setCompletedRows] = useState<Map<string, boolean>>(
    new Map()
  );
  const hasAutoMinimized = useRef(false);
  const { toggleExpand, isExpanded, closeToast } = useToastStore();

  // MDRS-267: this toast is mounted outside the router/ProtectedRoute tree, so
  // nothing tears it down when the session ends — it would sit there showing
  // stale "live" progress. Watch session validity and, on a genuine
  // active→inactive transition, close the modal, clear the dead session, and
  // redirect to re-authenticate.
  const isSessionActive = useIsSessionActive();
  const wasSessionActiveRef = useRef(false);
  const hasHandledExpiryRef = useRef(false);

  useEffect(() => {
    if (isSessionActive) {
      wasSessionActiveRef.current = true;
      return;
    }
    // Ignore an initial/bootstrap render that momentarily reports inactive —
    // only react once we've observed a live session go away.
    if (!wasSessionActiveRef.current || hasHandledExpiryRef.current) return;
    hasHandledExpiryRef.current = true;

    closeToast();
    // Not awaited. Only logoutUser's recent-searches clear runs synchronously
    // (MDRS-558); it then awaits the recovery-data clear (up to 1s) before
    // clearing the token and storage, so the hard redirect below can unload
    // the page before those steps run.
    logoutUser(false, "Bundle toast: session expired");
    // Hard redirect (not router navigation): the toast lives outside the router.
    window.location.href = "/";
  }, [isSessionActive, closeToast]);

  const handleComplete = useCallback((rowId: string, needsReview: boolean) => {
    setCompletedRows((prev) => new Map(prev).set(rowId, needsReview));
  }, []);

  // Auto-minimize toast once when all rows complete. The list stays mounted
  // across batches (MDRS-550), so a new batch re-arms it, only rows still
  // listed count towards needing review, and a picker the user just opened
  // isn't collapsed out from under them.
  const allComplete =
    rows.length > 0 && rows.every((row) => completedRows.has(row.id));
  const anyNeedsReview = rows.some((row) => completedRows.get(row.id) === true);

  useEffect(() => {
    if (!allComplete) {
      hasAutoMinimized.current = false;
      return;
    }
    if (anyNeedsReview || !isExpanded || isPickerShown || hasAutoMinimized.current) {
      return;
    }
    hasAutoMinimized.current = true;
    const timer = setTimeout(() => {
      toggleExpand();
    }, 3000);
    return () => clearTimeout(timer);
  }, [allComplete, anyNeedsReview, isExpanded, isPickerShown, toggleExpand]);

  if (rows.length === 0) return null;

  const groups = groupRowsByCase(rows, activeTarget, caseOpen);
  const handleOpenChange = (openCases: string[]) =>
    groups.forEach((group) => {
      const isOpen = openCases.includes(group.caseNumber);
      if (isOpen !== group.isOpen) setCaseOpen(group.caseNumber, isOpen);
    });

  return (
    <div className="-mb-4 max-h-[50vh] overflow-auto">
      <AccordionPrimitive.Root
        type="multiple"
        value={groups
          .filter((group) => group.isOpen)
          .map((group) => group.caseNumber)}
        onValueChange={handleOpenChange}
        className="space-y-3 pb-4 pr-2"
      >
        {groups.map((group) => (
          <CaseSection
            key={group.caseNumber}
            group={group}
            renderRow={(row, conflict) => (
              <FileItem
                key={row.id}
                file={row.file}
                userId={row.userId}
                caseNumber={row.caseNumber}
                initialState={row.initialState}
                onComplete={(needsReview) => handleComplete(row.id, needsReview)}
                onDismiss={() => removeRow(row.id)}
                onTaskCreated={(taskId) => setTaskId(row.id, taskId)}
                onBusyChange={(busy) => setBusy(row.id, busy)}
                onStatusChange={(status) => setStatus(row.id, status)}
                duplicate={conflict?.duplicate}
                isStartBlocked={conflict?.isStartBlocked}
              />
            )}
          />
        ))}
      </AccordionPrimitive.Root>
    </div>
  );
};
